import { ReactNode, useCallback, useEffect, useRef } from 'react';

interface InfiniteScrollWrapperProps {
	className?: string;
	children: ReactNode;
	onNearEnd?: () => void;
	loadBefore?: number;
	currentRecordsLength: number;
}

const InfiniteScrollWrapper = ({
	className = '',
	children,
	onNearEnd,
	loadBefore = 300,
	currentRecordsLength,
}: InfiniteScrollWrapperProps) => {
	const bufferRef = useRef<HTMLDivElement>(null);

	const nearEndFuncRef = useRef(() => {});

	const handleNearEndReached = useCallback(() => {
		nearEndFuncRef?.current();
	}, []);

	const handleObserverCallback = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const target = entries[0];

			if (target.isIntersecting) handleNearEndReached();
		},
		[handleNearEndReached]
	);

	const findScrollParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
		if (!element) return null;

		const style = window.getComputedStyle(element);
		const overflowY = style.getPropertyValue('overflow-y');

		if (overflowY === 'auto' || overflowY === 'scroll' || element === document.documentElement) {
			return element;
		}

		return findScrollParent(element.parentElement);
	}, []);

	const handleContainerScroll = useCallback((event: Event) => {
		const scrollContainer = event.target as HTMLElement;
		if (!scrollContainer) return;

		const scrollPosition = scrollContainer?.scrollTop;
		const containerHeight = scrollContainer?.clientHeight;
		const scrollHeight = scrollContainer?.scrollHeight;

		const diff = Math.abs(scrollHeight - scrollPosition - containerHeight);
		const endReached = diff < 4;

		if (endReached) handleNearEndReached();
	}, []);

	useEffect(() => {
		nearEndFuncRef.current = typeof onNearEnd === 'function' ? onNearEnd : () => {};
	}, [onNearEnd]);

	useEffect(() => {
		const scrollContainer = findScrollParent(bufferRef.current);
		const options = {
			root: scrollContainer, // Use the nearest scrollable container as root
			rootMargin: `0px 0px ${loadBefore}px 0px`,
			threshold: 0.1,
		};
		const observer = new IntersectionObserver(handleObserverCallback, options);
		const buffer = bufferRef.current;

		if (buffer) observer.observe(buffer);

		if (currentRecordsLength > 0) {
			if (scrollContainer) handleContainerScroll({ target: scrollContainer } as unknown as Event);
		}

		if (scrollContainer) scrollContainer?.addEventListener('scroll', handleContainerScroll);

		return () => {
			scrollContainer?.removeEventListener('scroll', handleContainerScroll);
			if (buffer) observer.unobserve(buffer);
		};
	}, [currentRecordsLength]);

	return (
		<div
			className={`relative ${className} pb-2`}
			data-infinite-scroll-wrapper
		>
			{children}

			<div
				ref={bufferRef}
				className="pointer-events-none p-0 opacity-0"
			/>
		</div>
	);
};

export default InfiniteScrollWrapper;
